import { unreachable } from "./unreachable";
import { CollectorInput, DeviceOutput, SiteReadable } from "../services/client";

type DeviceType = NonNullable<DeviceOutput["type"]>;

type DeviceTypeDistribution = {
  [K in DeviceType]: string[];
};
export const getDeviceDistribution = (
  site: SiteReadable,
): DeviceTypeDistribution | undefined => {
  return site.configurations?.site_configuration?.device_list.reduce(
    (acc, device) => {
      if (device.type == null) {
        return acc;
      }

      if (device.type in acc) {
        acc[device.type].push(device.name);
      }
      return acc;
    },
    {
      forecasted_series_3phase: [],
      charging_pole_3phase: [],
      dc_charging_pole: [],
      battery_energy_storage_system: [],
      double_charger: [],
    } satisfies DeviceTypeDistribution as DeviceTypeDistribution,
  );
};

/**
 * Asset names of every charger on the site. Each connector of a double
 * charger is its own asset, so it is listed instead of the charger itself.
 */
export const getChargingAssetNames = (site: SiteReadable): string[] =>
  site.configurations?.site_configuration?.device_list.flatMap((device) => {
    switch (device.type) {
      case "charging_pole_3phase":
      case "dc_charging_pole":
        return [device.name];
      case "double_charger":
        return device.connectors.map((connector) => connector.name);
      case "forecasted_series_3phase":
      case "battery_energy_storage_system":
      case undefined:
        return [];
      default:
        return unreachable(device);
    }
  }) ?? [];

const getChildren = (collector: CollectorInput): string[] => {
  if (!collector.children) {
    return [collector.name];
  }

  return [collector.name].concat(...collector.children.map(getChildren));
};

export const getAllCollectors = (site: SiteReadable): string[] | undefined => {
  return site.configurations?.site_configuration?.collector_list?.flatMap(
    (collector) => getChildren(collector),
  );
};
